import { AssortmentPriceChangesFilter, DraftAffectedItemsTable } from '@retail/calculus/components';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { useMemo } from 'react';
import { DraftCalculusTablePermissions, DraftCondition } from '@retail/calculus/types';
import { Link, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { ArrowBack } from '@mui/icons-material';
import { useAppTFunction } from '@retail/app/i18n';
import { useFetchAffectedItems } from '@retail/calculus/data-access';
import { AssortmentPricesComparison } from '@retail/my-assortment/types';
import {
  InfoChip,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
  ToggleSwitch,
} from '@retail/components';
import { useDisclosure } from '@shared/hooks';
import { brandLogo } from '@retail/utils';

interface Props {
  supplierNumber: number;
  selectedConditionIds?: number[];
  updateConditionIds: (id: number) => void;
  draftConditions: DraftCondition[];
}

export function DraftAffectedItemsContainer({
  supplierNumber,
  selectedConditionIds,
  updateConditionIds,
  draftConditions,
}: Props) {
  const t = useAppTFunction();
  const { selectedBrand } = useUserBrands();
  const navigate = useNavigate();
  const { assortmentId, orgUnitType } = useSelectedOrgUnit();
  const includeVat = useDisclosure(true);
  const { getPermittedActions } = usePermissionsForCurrentUser();
  const salesPriceConditionsIsEnabled = useIsFeatureEnabled('sales-price-condition');

  const showFilters = useDisclosure();

  const permittedActions: DraftCalculusTablePermissions = useMemo(
    () => ({
      purchaseAgreements: getPermittedActions('PRICE_CALCULATION__PURCHASE_CONDITION'),
      freight: getPermittedActions('PRICE_CALCULATION__FREIGHT'),
      markup: getPermittedActions('PRICE_CALCULATION__MARKUP'),
      priceSuggestions: getPermittedActions('PRICE_CALCULATION__PRICE_SUGGESTIONS'),
    }),
    [getPermittedActions]
  );
  const { data: affectedItemsDto = [], isLoading } = useFetchAffectedItems({
    assortmentId,
    supplierNumber,
    conditionIds: selectedConditionIds ?? draftConditions.map((cond) => cond.condition.id),
    suspense: false,
  });
  const affectedItems = affectedItemsDto.map((dto) =>
    AssortmentPricesComparison.fromDTO(dto, {
      name: selectedBrand.name,
      id: selectedBrand.brandId,
      imgUrl: brandLogo(selectedBrand.brandId).logo,
    })
  );

  return (
    <Stack flexGrow={1} overflow="hidden">
      <Link
        color="text.primary"
        onClick={() => navigate(-1)}
        alignSelf="flex-start"
        sx={{
          '&:hover': {
            cursor: 'pointer',
          },
        }}
      >
        <Stack flexDirection="row" alignItems="center" p={1.5}>
          <ArrowBack fontSize="small" /> {t('common.goBack')}
        </Stack>
      </Link>
      <ToggleFiltersToolbar sx={{ justifyContent: 'space-between', mb: 1 }}>
        <Stack flexDirection="row" gap={1} alignItems="center">
          <Typography variant="subtitle1">{t('calculus.itemsAffected.affectedItems')}:</Typography>
          <InfoChip label={affectedItems.length} isLoading={isLoading} />
        </Stack>
        <Stack flexDirection="row" gap={1} alignItems="flex-end">
          <ToggleSwitch
            toggle={includeVat.isOpen}
            setToggle={includeVat.onToggle}
            text={t('calculus.priceWithVat')}
          />
          <ToggleFiltersButton
            showFilters={showFilters.isOpen}
            toggleFilters={showFilters.onToggle}
          />
        </Stack>
      </ToggleFiltersToolbar>
      <Stack flexDirection="row" flexGrow={1}>
        <DraftAffectedItemsTable
          items={affectedItems}
          includeVat={includeVat.isOpen}
          loading={isLoading}
          permissions={permittedActions}
          orgType={orgUnitType}
        />
        <AssortmentPriceChangesFilter
          showFilters={showFilters.isOpen}
          draftConditions={draftConditions?.map((cond) => cond.condition)}
          selectedConditionIds={selectedConditionIds ?? []}
          salesPriceConditionsIsEnabled={salesPriceConditionsIsEnabled}
          updateConditionIds={updateConditionIds}
        />
      </Stack>
    </Stack>
  );
}
